
/**
 * Utility functions for exam score validation and processing
 */

/**
 * Clean a score value by removing % sign and converting to a number
 * If the value is a percentage string (e.g., "80%"), it returns just the numeric value
 * 
 * @param value - The score value to clean
 * @returns The cleaned numeric value
 */
export const cleanScoreValue = (value: any): number => {
  if (value === undefined || value === null) return 0;
  
  if (typeof value === 'string') {
    // Check if the string contains a percentage sign
    if (value.includes('%')) {
      // We're just extracting the number here, not converting to actual score
      // That conversion happens in the form handlers
      return parseFloat(value.replace(/%/g, '')) || 0;
    }
    // Regular number string
    return parseFloat(value) || 0;
  }
  return typeof value === 'number' ? value : 0;
};

/**
 * Calculate percentage from obtained and maximum scores
 * 
 * @param obtained - The obtained score
 * @param maximum - The maximum possible score
 * @returns The percentage as a string with one decimal place
 */
export const calculatePercentage = (obtained: number | string, maximum: number | string): string => {
  // Handle if obtained is a percentage string directly
  if (typeof obtained === 'string' && obtained.includes('%')) {
    return parseFloat(obtained.replace(/%/g, '')).toFixed(1);
  }
  
  const obtainedValue = cleanScoreValue(obtained);
  const maximumValue = cleanScoreValue(maximum);
  
  if (maximumValue <= 0) return '0.0';
  
  return ((obtainedValue / maximumValue) * 100).toFixed(1);
};

/**
 * Validates if a string is a valid score (can include % sign)
 * 
 * @param value - The value to validate
 * @returns True if valid score
 */
export const isValidScore = (value: string | number): boolean => {
  if (typeof value === 'number') return !isNaN(value);
  
  // If it's a string, clean it first
  const cleanValue = value.replace(/%/g, '').trim();
  return !isNaN(parseFloat(cleanValue));
};

/**
 * Detects if a value is a percentage and returns the actual score based on maximum
 * 
 * @param value - The input value (may include % sign)
 * @param maximum - The maximum possible score
 * @returns The calculated actual score
 */
export const detectPercentageAndConvert = (value: string | number, maximum: number): number => {
  if (typeof value === 'string' && value.includes('%')) {
    const percentValue = parseFloat(value.replace(/%/g, ''));
    if (!isNaN(percentValue)) {
      return (percentValue / 100) * maximum;
    }
  }
  return cleanScoreValue(value);
};
